/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useFormikContext, useField } from 'formik';
import { InputAdornment, Zoom, makeStyles } from '@material-ui/core';
import { Check, Block } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { PhoneMask, ZipCodeMask, Numbers } from './InputMasks';

const useStyles = makeStyles(() => ({
  input: {
    '&[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
}));
const CustomInputField = ({
  name,
  autoFocus,
  startAdornment,
  customInput: CustomInput,
  InputProps,
  extraEndAdornments,
  maskType,
  helperText,
  capitalize,
  trim,
  trimLeadingZeros,
  removeArrows,
  onChange,
  showError,
  onBlur,
  disableNativeOnChange,
  ...rest
}) => {
  const classes = useStyles();
  const { dirty, handleBlur: formikHandleBlur } = useFormikContext();
  const [field, meta] = useField(name);
  const [selected, setSelected] = React.useState(false);
  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
    formikHandleBlur(e);
    setSelected(false);
  };
  const handleFocus = () => {
    setSelected(true);
  };

  const handleChange = (e) => {
    const handleValue = (value) => {
      if (capitalize && value.match(/^[a-z]/)) {
        return value.toUpperCase();
      }
      if (trim) {
        return value.trim();
      }
      if (trimLeadingZeros && field.type === 'number') {
        return value.replace(/^0+/, '');
      }
      return e.currentTarget.value;
    };
    const event = {
      target: {
        name: field.name,
        value: handleValue(e.currentTarget.value),
      },
    };
    if (onChange) {
      onChange(event);
    }
    if (!disableNativeOnChange) {
      field.onChange(event);
    }
  };
  const isError = meta.touched && meta.error;
  return (
    <CustomInput
      {...rest}
      name={name}
      onBlur={handleBlur}
      onFocus={handleFocus}
      helperText={meta.touched && meta.error ? meta.error : helperText}
      FormHelperTextProps={
        !showError && isError ? { style: { display: 'none' } } : {}
      }
      InputProps={{
        ...InputProps,
        onChange: handleChange,
        ...(maskType === 'phone' && {
          onChange: handleChange,
          inputComponent: PhoneMask,
        }),
        ...(maskType === 'zipcode' && {
          onChange: handleChange,
          inputComponent: ZipCodeMask,
        }),
        ...(maskType === 'numbers' && {
          onChange: handleChange,
          inputComponent: Numbers,
        }),
        inputProps: {
          ...InputProps.inputProps,
          onChange,
          className: removeArrows
            ? [InputProps.inputProps?.className, classes.input]
                .filter(Boolean)
                .join(' ')
            : InputProps.inputProps?.className || '',
        },
        autoFocus: !!autoFocus,
        startAdornment: startAdornment && (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ),
        endAdornment: extraEndAdornments && (
          <>
            <InputAdornment position="end" style={{ minWidth: '25px' }}>
              {extraEndAdornments === true ? (
                <>
                  {!meta.error && dirty && (
                    <Zoom
                      in={meta.touched && !meta.error && dirty}
                      timeout={300}
                    >
                      <Check color={selected ? 'primary' : 'disabled'} />
                    </Zoom>
                  )}
                  {meta.error && meta.touched && (
                    <Zoom in={meta.error && meta.touched} timeout={300}>
                      <Block color="error" />
                    </Zoom>
                  )}
                </>
              ) : (
                extraEndAdornments
              )}
            </InputAdornment>
          </>
        ),
      }}
    />
  );
};

CustomInputField.defaultProps = {
  autoFocus: false,
  startAdornment: null,
  InputProps: {},
  extraEndAdornments: null,
  maskType: '',
  helperText: null,
  capitalize: false,
  trim: false,
  trimLeadingZeros: false,
  removeArrows: false,
  onChange: null,
  showError: true,
  onBlur: null,
  disableNativeOnChange: false,
};

CustomInputField.propTypes = {
  name: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  startAdornment: PropTypes.node,
  customInput: PropTypes.elementType.isRequired,
  InputProps: PropTypes.objectOf(PropTypes.any),
  extraEndAdornments: PropTypes.node,
  maskType: PropTypes.string,
  helperText: PropTypes.node,
  capitalize: PropTypes.bool,
  trim: PropTypes.bool,
  removeArrows: PropTypes.bool,
  trimLeadingZeros: PropTypes.bool,
  onChange: PropTypes.func,
  showError: PropTypes.bool,
  onBlur: PropTypes.func,
  disableNativeOnChange: PropTypes.bool,
};

export default CustomInputField;
